import { useState, useCallback, useEffect, type KeyboardEvent } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Calendar, Search, ChevronDown, X, User, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { getDateLocale } from "@/lib/i18n/formatLocale";
import { moroccanCities } from "@/lib/data/moroccan-cities";
import { DateRange } from "react-day-picker";
import { cn } from "@/lib/utils";
import { EnhancedDatePicker } from "@/components/ui/enhanced-date-picker";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { LocationSelector } from "@/components/search/LocationSelector";
import { trackEvent, isPostHogReady } from "@/lib/services/posthog.service";
import {
  SEARCH_TIME_OPTIONS,
  DRIVER_AGE_OPTIONS,
  DEFAULT_PICKUP_TIME,
  DEFAULT_RETURN_TIME,
  buildSearchUrl,
  resolveDateRangeFromSearchParams,
  resolveInitialLocation,
  resolveReturnLocation,
  resolveTimeParam,
  resolveDriverAge,
} from "@/components/search/searchBarUtils";

export { resolveInitialLocation } from "@/components/search/searchBarUtils";

const RECENT_SEARCHES_KEY = "recent_searches";
const MAX_RECENT_SEARCHES = 5;
const POPULAR_CITIES = ["Casablanca", "Marrakech", "Rabat", "Fès", "Tanger", "Agadir"];

interface SearchBarProps {
  compact?: boolean;
  /** Présentation plus dense pour le hero mobile, sans modifier les interactions. */
  mobileDense?: boolean;
  /** Ville préremplie (pages SEO ville / intent) quand l’URL n’a pas encore location/city */
  defaultLocation?: string;
  className?: string;
}

const SearchBar = ({
  compact = false,
  mobileDense = false,
  defaultLocation,
  className,
}: SearchBarProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const dateLocale = getDateLocale(i18n.language);

  const [locationValue, setLocationValue] = useState(() =>
    resolveInitialLocation(searchParams, defaultLocation)
  );
  const [returnLocationValue, setReturnLocationValue] = useState(() =>
    resolveReturnLocation(searchParams, resolveInitialLocation(searchParams, defaultLocation))
  );
  const [differentReturn, setDifferentReturn] = useState(() => {
    const returnLoc = searchParams.get("returnLocation")?.trim() || searchParams.get("dropoff")?.trim();
    const pickup = resolveInitialLocation(searchParams, defaultLocation);
    return Boolean(returnLoc && returnLoc !== pickup);
  });
  const [locationOpen, setLocationOpen] = useState(false);
  const [returnLocationOpen, setReturnLocationOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    resolveDateRangeFromSearchParams(searchParams)
  );
  const [pickupTime, setPickupTime] = useState(() =>
    resolveTimeParam(searchParams, "pickupTime", DEFAULT_PICKUP_TIME)
  );
  const [returnTime, setReturnTime] = useState(() =>
    resolveTimeParam(searchParams, "returnTime", DEFAULT_RETURN_TIME)
  );
  const [driverAge, setDriverAge] = useState(() => resolveDriverAge(searchParams));
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(RECENT_SEARCHES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const openLocation = () => setLocationOpen(true);
    const openDates = () => setDateOpen(true);
    window.addEventListener("rakb:open-search-location", openLocation);
    window.addEventListener("rakb:open-search-dates", openDates);
    return () => {
      window.removeEventListener("rakb:open-search-location", openLocation);
      window.removeEventListener("rakb:open-search-dates", openDates);
    };
  }, []);

  useEffect(() => {
    if (searchParams.get("focus") !== "location") return;

    setLocationOpen(true);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("focus");
    const nextSearch = nextParams.toString();
    const nextPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    if (location.pathname + location.search !== nextPath) {
      navigate(nextPath, { replace: true });
    }
  }, [searchParams, location.pathname, location.search, navigate]);

  useEffect(() => {
    const fromUrl =
      searchParams.get("location")?.trim() || searchParams.get("city")?.trim() || "";
    if (fromUrl) {
      setLocationValue(fromUrl);
    } else if (defaultLocation?.trim()) {
      setLocationValue(defaultLocation.trim());
    }

    const returnFromUrl =
      searchParams.get("returnLocation")?.trim() || searchParams.get("dropoff")?.trim() || "";
    if (returnFromUrl) {
      setReturnLocationValue(returnFromUrl);
      setDifferentReturn(returnFromUrl !== (fromUrl || defaultLocation?.trim() || ""));
    }

    setPickupTime(resolveTimeParam(searchParams, "pickupTime", DEFAULT_PICKUP_TIME));
    setReturnTime(resolveTimeParam(searchParams, "returnTime", DEFAULT_RETURN_TIME));
    setDriverAge(resolveDriverAge(searchParams));

    const nextRange = resolveDateRangeFromSearchParams(searchParams);
    if (nextRange?.from && nextRange?.to) {
      setDateRange(nextRange);
    }
  }, [searchParams, defaultLocation]);

  const addToRecentSearches = useCallback((city: string) => {
    try {
      const updated = [city, ...recentSearches.filter((s) => s !== city)].slice(
        0,
        MAX_RECENT_SEARCHES
      );
      setRecentSearches(updated);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (error) {
      console.error("Failed to save recent searches:", error);
    }
  }, [recentSearches]);

  const handleCitySelect = useCallback(
    (city: string) => {
      setLocationValue(city);
      setLocationOpen(false);
      if (!differentReturn) {
        setReturnLocationValue(city);
      }
      addToRecentSearches(city);
      if (isPostHogReady()) {
        trackEvent("location_selected", {
          location: city,
          has_dates: !!(dateRange?.from && dateRange?.to),
        });
      }
      if (!(dateRange?.from && dateRange?.to)) {
        requestAnimationFrame(() => setDateOpen(true));
      }
    },
    [addToRecentSearches, dateRange, differentReturn]
  );

  const handleReturnCitySelect = useCallback(
    (city: string) => {
      setReturnLocationValue(city);
      setReturnLocationOpen(false);
      addToRecentSearches(city);
    },
    [addToRecentSearches]
  );

  const handleDifferentReturnChange = useCallback(
    (checked: boolean) => {
      setDifferentReturn(checked);
      if (!checked) {
        setReturnLocationValue(locationValue);
        setReturnLocationOpen(false);
      }
    },
    [locationValue]
  );

  const getDateDisplay = useCallback(
    (which: "from" | "to" | "range") => {
      if (which === "from") {
        if (!dateRange?.from) return t("search.addDates");
        return format(dateRange.from, "d MMM", { locale: dateLocale });
      }
      if (which === "to") {
        if (!dateRange?.to) return t("search.addDates");
        return format(dateRange.to, "d MMM", { locale: dateLocale });
      }
      if (!dateRange?.from) return t("search.addDates");
      if (!dateRange.to) return format(dateRange.from, "d MMM", { locale: dateLocale });
      return `${format(dateRange.from, "d MMM", { locale: dateLocale })} - ${format(dateRange.to, "d MMM", { locale: dateLocale })}`;
    },
    [dateRange, dateLocale, t]
  );

  const performSearch = useCallback(() => {
    if (!locationValue.trim()) {
      setLocationOpen(true);
      return;
    }

    if (differentReturn && !returnLocationValue.trim()) {
      toast({
        title: t("search.selectReturnCity"),
        description: t("search.selectReturnCityDesc"),
        variant: "destructive",
      });
      setReturnLocationOpen(true);
      return;
    }

    setIsSearching(true);

    if (isPostHogReady() && dateRange?.from && dateRange?.to) {
      trackEvent("date_selected", {
        start_date: format(dateRange.from, "yyyy-MM-dd"),
        end_date: format(dateRange.to, "yyyy-MM-dd"),
        location: locationValue || undefined,
      });
    }

    const searchUrl = buildSearchUrl({
      location: locationValue,
      dateRange,
      returnLocation: returnLocationValue,
      differentReturnLocation: differentReturn,
      pickupTime: compact ? resolveTimeParam(searchParams, "pickupTime", DEFAULT_PICKUP_TIME) : pickupTime,
      returnTime: compact ? resolveTimeParam(searchParams, "returnTime", DEFAULT_RETURN_TIME) : returnTime,
      driverAge: compact ? resolveDriverAge(searchParams) : driverAge,
    });

    if (location.pathname + location.search !== searchUrl) {
      navigate(searchUrl);
    }

    setIsSearching(false);
    addToRecentSearches(locationValue);
  }, [
    locationValue,
    returnLocationValue,
    differentReturn,
    dateRange,
    pickupTime,
    returnTime,
    driverAge,
    compact,
    searchParams,
    navigate,
    location,
    t,
    toast,
    addToRecentSearches,
  ]);

  const handleRangeCommitted = useCallback(
    (range: DateRange) => {
      setDateRange(range);
      setDateOpen(false);
      if (!locationValue.trim()) return;

      if (isPostHogReady() && range.from && range.to) {
        trackEvent("date_selected", {
          start_date: format(range.from, "yyyy-MM-dd"),
          end_date: format(range.to, "yyyy-MM-dd"),
          location: locationValue || undefined,
        });
      }

      const searchUrl = buildSearchUrl({
        location: locationValue,
        dateRange: range,
        returnLocation: returnLocationValue,
        differentReturnLocation: differentReturn,
        pickupTime: compact ? resolveTimeParam(searchParams, "pickupTime", DEFAULT_PICKUP_TIME) : pickupTime,
        returnTime: compact ? resolveTimeParam(searchParams, "returnTime", DEFAULT_RETURN_TIME) : returnTime,
        driverAge: compact ? resolveDriverAge(searchParams) : driverAge,
      });

      if (location.pathname + location.search !== searchUrl) {
        navigate(searchUrl);
      }
      addToRecentSearches(locationValue);
    },
    [
      locationValue,
      returnLocationValue,
      differentReturn,
      pickupTime,
      returnTime,
      driverAge,
      compact,
      searchParams,
      navigate,
      location,
      addToRecentSearches,
    ]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        performSearch();
      }
    },
    [performSearch]
  );

  const locationLabels = {
    where: t("search.pickup"),
    anywhere: t("search.anywhere"),
    placeholder: t("search.placeholderShort"),
    recentSearches: t("search.recentSearches"),
    popularChoices: t("search.popularChoices"),
    results: t("search.results"),
    noResults: (query: string) => t("search.noResults", { query }),
    clearLabel: t("search.clearLabel"),
    noCityAvailable: t("search.noCityAvailable"),
  };

  const returnLocationLabels = {
    ...locationLabels,
    where: t("search.dropoff"),
  };

  const fieldSegmentClass = cn(
    "flex h-[3.75rem] w-full min-w-0 items-center rounded-xl transition-colors duration-200 hover:bg-neutral-50/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night/25 focus-visible:ring-offset-2",
    mobileDense ? "gap-2 px-3 sm:gap-2.5 sm:px-3.5" : "gap-2.5 px-3.5"
  );

  const fieldLabelClass = "text-[11px] font-medium leading-none text-neutral-500";
  const fieldValueClass = "truncate text-sm font-semibold leading-5 text-neutral-900";
  const fieldStackClass = "flex min-w-0 flex-1 flex-col justify-center gap-1 text-start";

  const selectTriggerClass = cn(
    "h-5 w-full justify-start gap-1 border-0 bg-transparent p-0 shadow-none",
    "text-sm font-semibold leading-5 text-neutral-900",
    "focus:ring-0 focus:ring-offset-0",
    "[&>span]:line-clamp-1 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:shrink-0 [&>svg]:opacity-50"
  );

  if (compact) {
    return (
      <div className={cn("mx-auto w-full max-w-full", className)}>
        <div className="rounded-2xl border border-border/70 bg-card p-1.5 shadow-soft">
          <div className="flex flex-row items-center gap-1">
            <LocationSelector
              value={locationValue}
              onSelect={handleCitySelect}
              onClear={() => setLocationValue("")}
              open={locationOpen}
              onOpenChange={setLocationOpen}
              popularCities={POPULAR_CITIES}
              allCities={moroccanCities}
              recentSearches={recentSearches}
              compact
              t={{
                where: t("search.where"),
                anywhere: t("search.anywhere"),
                placeholder: t("search.placeholderShort"),
                recentSearches: t("search.recentSearches"),
                popularChoices: t("search.popularChoices"),
                results: t("search.results"),
                noResults: (query: string) => t("search.noResults", { query }),
                clearLabel: t("search.clearLabel"),
                noCityAvailable: t("search.noCityAvailable"),
              }}
            />
            <div className="hidden h-8 w-px shrink-0 bg-border sm:block" aria-hidden />
            <button
              type="button"
              onClick={() => setDateOpen(true)}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 rounded-xl px-2.5 py-2 text-start transition-colors hover:bg-scandi-bg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-night focus-visible:ring-offset-2 sm:px-3",
                dateOpen && "bg-scandi-bg"
              )}
              aria-pressed={dateOpen}
              aria-label={t("search.when")}
            >
              <Calendar
                className="h-4 w-4 shrink-0 text-text-tertiary"
                aria-hidden
              />
              <span className="min-w-0">
                <span className="hidden text-[11px] font-medium leading-none text-text-tertiary sm:block">
                  {t("search.when")}
                </span>
                <span
                  className={cn(
                    "block truncate text-sm font-medium leading-5",
                    dateRange?.from ? "text-text-primary" : "text-text-secondary"
                  )}
                >
                  {!dateRange?.from ? t("search.when") : getDateDisplay("range")}
                </span>
              </span>
            </button>
            <Button
              onClick={performSearch}
              disabled={isSearching}
              onKeyDown={handleKeyDown}
              aria-label={t("search.search")}
              className="flex h-10 shrink-0 items-center justify-center gap-2 rounded-xl bg-green-night px-3 text-sm font-semibold text-white hover:bg-green-night/90 disabled:cursor-not-allowed disabled:opacity-50 sm:h-11 sm:px-4"
            >
              {isSearching ? (
                <span
                  className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"
                  aria-hidden
                />
              ) : (
                <>
                  <Search className="h-4 w-4" aria-hidden />
                  <span className="hidden sm:inline">{t("search.search")}</span>
                </>
              )}
            </Button>
          </div>
        </div>
        <EnhancedDatePicker
          open={dateOpen}
          onOpenChange={setDateOpen}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onConfirm={() => {
            setDateOpen(false);
            if (locationValue) performSearch();
          }}
          onRangeCommitted={handleRangeCommitted}
          showSearch={!!locationValue}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "mx-auto w-full max-w-5xl md:px-0",
        mobileDense ? "px-0" : "px-4",
        className
      )}
    >
      <div
        className={cn(
          "bg-white shadow-[0_12px_48px_rgba(0,0,0,0.14),0_2px_8px_rgba(0,0,0,0.06)] ring-1 ring-black/[0.06] backdrop-blur-md transition-shadow duration-300 ease-out hover:shadow-[0_16px_52px_rgba(0,0,0,0.16)] focus-within:shadow-[0_16px_56px_rgba(4,120,87,0.12)] focus-within:ring-green-night/20",
          mobileDense
            ? "rounded-[1.25rem] p-2 sm:rounded-2xl sm:p-3"
            : "rounded-2xl p-3 sm:p-3.5"
        )}
      >
        {/* Toggle restitution autre lieu */}
        <div className="mb-2 flex items-center justify-between gap-3 px-1 sm:mb-2.5">
          <label
            htmlFor="different-return-location"
            className="cursor-pointer text-xs font-medium text-neutral-600 sm:text-[13px]"
          >
            {t("search.differentReturnLocation")}
          </label>
          <Switch
            id="different-return-location"
            checked={differentReturn}
            onCheckedChange={handleDifferentReturnChange}
            aria-label={t("search.differentReturnLocation")}
          />
        </div>

        <div className="flex flex-col gap-2">
          {/* Lieux */}
          <div
            className={cn(
              "grid gap-2",
              differentReturn ? "md:grid-cols-2" : "grid-cols-1"
            )}
          >
            <div className="w-full min-w-0 overflow-hidden rounded-xl border border-neutral-100 bg-neutral-50/60">
              <LocationSelector
                value={locationValue}
                onSelect={handleCitySelect}
                onClear={() => {
                  setLocationValue("");
                  if (!differentReturn) setReturnLocationValue("");
                }}
                open={locationOpen}
                onOpenChange={setLocationOpen}
                popularCities={POPULAR_CITIES}
                allCities={moroccanCities}
                recentSearches={recentSearches}
                segmentClassName={fieldSegmentClass}
                t={locationLabels}
              />
            </div>
            {differentReturn && (
              <div className="w-full min-w-0 overflow-hidden rounded-xl border border-neutral-100 bg-neutral-50/60">
                <LocationSelector
                  value={returnLocationValue}
                  onSelect={handleReturnCitySelect}
                  onClear={() => setReturnLocationValue("")}
                  open={returnLocationOpen}
                  onOpenChange={setReturnLocationOpen}
                  popularCities={POPULAR_CITIES}
                  allCities={moroccanCities}
                  recentSearches={recentSearches}
                  segmentClassName={fieldSegmentClass}
                  t={returnLocationLabels}
                />
              </div>
            )}
          </div>

          {/* Each date stays with its time. Age and search share the last row from small screens up. */}
          <div className="flex flex-col gap-2">
          <div className="grid gap-2 md:grid-cols-2">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(6.25rem,7.5rem)] gap-2">
            {/* Pickup date */}
            <div
              className={cn(
                fieldSegmentClass,
                "border bg-neutral-50/60",
                dateOpen || dateRange?.from
                  ? "border-green-night ring-2 ring-green-night/40"
                  : "border-neutral-100"
              )}
            >
              <button
                type="button"
                onClick={() => setDateOpen(true)}
                className="flex min-w-0 flex-1 items-center gap-2.5 bg-transparent text-start"
                aria-pressed={Boolean(dateOpen || dateRange?.from)}
                aria-label={t("search.pickupDate")}
              >
                <Calendar
                  className={cn(
                    "h-4 w-4 shrink-0 sm:h-5 sm:w-5",
                    dateRange?.from ? "text-green-night" : "text-neutral-400"
                  )}
                  aria-hidden
                />
                <div className={fieldStackClass}>
                  <p className={fieldLabelClass}>{t("search.pickupDate")}</p>
                  <p className={cn(fieldValueClass, !dateRange?.from && "text-neutral-500")}>
                    {getDateDisplay("from")}
                  </p>
                </div>
                {!dateRange?.from && (
                  <ChevronDown className="h-4 w-4 shrink-0 text-neutral-400" aria-hidden />
                )}
              </button>
              {dateRange?.from ? (
                <button
                  type="button"
                  onClick={() => setDateRange(undefined)}
                  className="shrink-0 rounded-full p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  aria-label={t("search.clearLabel")}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              ) : null}
            </div>

            {/* Pickup time */}
            <div className={cn(fieldSegmentClass, "border border-neutral-100 bg-neutral-50/60")}>
              <Clock className="hidden h-4 w-4 shrink-0 text-neutral-400 sm:block sm:h-5 sm:w-5" aria-hidden />
              <div className={fieldStackClass}>
                <p className={fieldLabelClass}>{t("search.pickupTime")}</p>
                <Select value={pickupTime} onValueChange={setPickupTime}>
                  <SelectTrigger className={selectTriggerClass} aria-label={t("search.pickupTime")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SEARCH_TIME_OPTIONS.map((time) => (
                      <SelectItem key={`pickup-${time}`} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-[minmax(0,1fr)_minmax(6.25rem,7.5rem)] gap-2">
            {/* Return date */}
            <button
              type="button"
              onClick={() => setDateOpen(true)}
              className={cn(
                fieldSegmentClass,
                "border bg-neutral-50/60",
                dateOpen || dateRange?.to
                  ? "border-green-night ring-2 ring-green-night/40"
                  : "border-neutral-100"
              )}
              aria-pressed={Boolean(dateOpen || dateRange?.to)}
              aria-label={t("search.returnDate")}
            >
              <Calendar
                className={cn(
                  "h-4 w-4 shrink-0 sm:h-5 sm:w-5",
                  dateRange?.to ? "text-green-night" : "text-neutral-400"
                )}
                aria-hidden
              />
              <div className={fieldStackClass}>
                <p className={fieldLabelClass}>{t("search.returnDate")}</p>
                <p className={cn(fieldValueClass, !dateRange?.to && "text-neutral-500")}>
                  {getDateDisplay("to")}
                </p>
              </div>
            </button>

            {/* Return time */}
            <div className={cn(fieldSegmentClass, "border border-neutral-100 bg-neutral-50/60")}>
              <Clock className="hidden h-4 w-4 shrink-0 text-neutral-400 sm:block sm:h-5 sm:w-5" aria-hidden />
              <div className={fieldStackClass}>
                <p className={fieldLabelClass}>{t("search.returnTime")}</p>
                <Select value={returnTime} onValueChange={setReturnTime}>
                  <SelectTrigger className={selectTriggerClass} aria-label={t("search.returnTime")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {SEARCH_TIME_OPTIONS.map((time) => (
                      <SelectItem key={`return-${time}`} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          </div>

            {/* Driver age */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-stretch">
            <div className={cn(fieldSegmentClass, "border border-neutral-100 bg-neutral-50/60")}>
              <User className="h-4 w-4 shrink-0 text-neutral-400 sm:h-5 sm:w-5" aria-hidden />
              <div className={fieldStackClass}>
                <p className={fieldLabelClass}>{t("search.driverAge.label")}</p>
                <Select value={driverAge} onValueChange={setDriverAge}>
                  <SelectTrigger className={selectTriggerClass} aria-label={t("search.driverAge.label")}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DRIVER_AGE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* CTA */}
            <Button
              onClick={performSearch}
              disabled={isSearching}
              onKeyDown={handleKeyDown}
              aria-label={t("search.searchBtn")}
              className={cn(
                "h-[3.75rem] w-full shrink-0 gap-2 rounded-xl bg-green-night px-5 text-sm font-semibold text-white shadow-md shadow-green-night/25 transition-all hover:bg-primary-hover hover:text-white active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:min-w-[11.5rem] sm:px-6 [&_span]:!text-white [&_svg]:!text-white",
                mobileDense && "text-sm sm:text-base"
              )}
            >
              {isSearching ? (
                <span className="flex items-center justify-center gap-2 !text-white">
                  <span
                    className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"
                    aria-hidden
                  />
                  <span className="!text-white">{t("search.searching")}</span>
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2 !text-white">
                  <Search className="h-5 w-5 !text-white" aria-hidden />
                  <span className="!text-white">{t("search.searchBtn")}</span>
                </span>
              )}
            </Button>
            </div>
          </div>
        </div>
      </div>

      <EnhancedDatePicker
        open={dateOpen}
        onOpenChange={setDateOpen}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        onConfirm={() => {
          setDateOpen(false);
          if (locationValue) performSearch();
        }}
        onRangeCommitted={handleRangeCommitted}
        showSearch={!!locationValue}
      />
    </div>
  );
};

export default SearchBar;
